import { createSlice } from "@reduxjs/toolkit";
import {
  SELECT_PRODUCTS_BASED_ON_MULTIPLE_CONDITIONS,
  SELECT_PRODUCTS_BASED_ON_SPECIFIC
} from "../../../constants/selectProductsBasedOn.js";
import { TAGS_SELECTION_LOGIC_EXCLUDE } from "../../../constants/tagsSelectionLogic.js";
import { ATTRIBUTE_DESCRIPTION, ATTRIBUTE_TITLE } from "../../../constants/attributeTypes.js";
import { PRODUCTS_NO_ACTION } from "../../../constants/productActionTypes.js";
import { UPDATES_FREQUENCY_EVERY_PERIOD } from "../../../constants/updatesFrequencyTypes.js";
import { TIME_1_DAY, TIME_7_DAY } from "../../../constants/timeInMs.js";
import { ATTRIBUTE_QUALIFIER_EQUAL, ATTRIBUTE_QUALIFIER_IS_EMPTY } from "../../../constants/attributeQualifierTypes.js";
import { DO_NOT_REDIRECT } from "../../../constants/redirectTypes.js";

const initialState = {
  currentStep: 0,
  actionId: null,
  actionData: {
    type: null,
    name: null,
    updatesFrequency: {
      type: UPDATES_FREQUENCY_EVERY_PERIOD,
      runActionEvery: TIME_1_DAY,
      disableAction: false,
      scheduledEveryDays: "0",
      selectedDate: new Date().toDateString(),
      selectedTime: {
        timeDirection: "AM",
        minutes: 0,
        hours: 0
      }
    },
    selectedProducts: {
      stockSelectedProducts: {
        selectProductsBasedOn: SELECT_PRODUCTS_BASED_ON_SPECIFIC,
        tagsSelectionLogic: TAGS_SELECTION_LOGIC_EXCLUDE,
        selectedTags: [],
        selectedSpecificItems: [],
        exceptionList: []
      },
      attributesSelectedProducts: {
        selectProductsBasedOn: SELECT_PRODUCTS_BASED_ON_MULTIPLE_CONDITIONS,
        missingAttributeCondition: {
          attribute: ATTRIBUTE_DESCRIPTION,
          qualifier: ATTRIBUTE_QUALIFIER_IS_EMPTY,
          value: ""
        },
        exceptionList: [],
        specificAttributeCondition: { attribute: ATTRIBUTE_TITLE, qualifier: ATTRIBUTE_QUALIFIER_EQUAL, value: "" },
        selectedTags: [],
        multipleConditionsArray: [{
          conditions: [{
            attribute: ATTRIBUTE_TITLE,
            qualifier: ATTRIBUTE_QUALIFIER_EQUAL,
            value: ""
          }]
        }]
      }
    },
    options: {
      stockOptions: {
        outStockLevel: "1",
        outStockAction: PRODUCTS_NO_ACTION,
        outStockBadge: {
          isActive: false,
          text: "Out of stock",
          font: "inter",
          fontSize: "13",
          backgroundColor: "#000000",
          textColor: "#FFFFFF"
        },
        outStockNotify: {
          isActive: false,
          frequency: TIME_7_DAY,
          email: ""
        },
        lowStockAction: PRODUCTS_NO_ACTION,
        lowStockBadge: {
          isActive: false,
          text: "Low Stock",
          font: "inter",
          fontSize: "13",
          backgroundColor: "#000000",
          textColor: "#FFFFFF"
        },
        lowStockNotify: {
          isActive: false,
          frequency: TIME_7_DAY,
          email: ""
        },
        lowStockLevelFrom: "",
        lowStockLevelTo: "",
        backStockLevel: "0",
        backStockAction: PRODUCTS_NO_ACTION,
        backStockNotify: {
          isActive: false,
          frequency: TIME_7_DAY,
          email: ""
        }
      },
      attributesOptions: {
        basedOnAttributesAction: PRODUCTS_NO_ACTION,
        unusedProductsBasedOnAttributes: PRODUCTS_NO_ACTION
      }
    },
    advancedSetting: {
      locationSettings: {
        monitorAllLocations: true,
        selectedLocationIds: [],
        selectedLocationNames: []
      },
      hideProductSettings: {
        redirect: DO_NOT_REDIRECT,
        customRedirectUrl: "",
        isActiveTags: false,
        selectedTags: [],
        specificShopifyPage: [],
        specificShopifyBlog: [],
        specificProductPage: []
      },
      downProductSettings: {
        isActiveTags: false,
        selectedTags: []
      },
      backProductSettings: {
        isActiveVisibleTags: false,
        isActiveExcludeTags: false,
        visibleSelectedTags: [],
        excludeSelectedTags: []
      }
    }
  }
};

export const action = createSlice({
  name: "action",
  initialState,
  reducers: {
    nextStep(state) {
      state.currentStep++;
    },
    setStep(state, action) {
      state.currentStep = action.payload
    },
    updateAction(_, action) {
      return action.payload
    },
    updateActionData(state, action) {
      state.actionData = { ...state.actionData, ...action.payload };
    },
    updateStockSelectedProducts(state, action) {
      state.actionData.selectedProducts.stockSelectedProducts = action.payload;
    },
    updateAttributesSelectedProducts(state, action) {
      state.actionData.selectedProducts.attributesSelectedProducts = action.payload;
    },
    updateStockOptions(state, action) {
      state.actionData.options.stockOptions = action.payload;
    },
    updateAttributesOptions(state, action) {
      state.actionData.options.attributesOptions = action.payload;
    },
    updateAdvancedSetting(state, action) {
      state.actionData.advancedSetting = action.payload;
    },
    updateUpdatesFrequency(state, action) {
      state.actionData.updatesFrequency = action.payload;
    },
    resetOptionsAndFrequency(state) {
      state.actionData.options = initialState.actionData.options;
      state.actionData.updatesFrequency = initialState.actionData.updatesFrequency;
    },
    resetAdvancedSettings(state) {
      state.actionData.advancedSetting = initialState.actionData.advancedSetting;
    },
    resetAction() {
      return initialState;
    }
  }
});

export const {
  nextStep,
  updateActionData,
  updateStockOptions,
  updateAdvancedSetting,
  resetAction,
  resetOptionsAndFrequency,
  resetAdvancedSettings,
  updateStockSelectedProducts,
  updateAttributesSelectedProducts,
  updateAttributesOptions,
  updateUpdatesFrequency,
  setStep,
  updateAction
} = action.actions;

export default action.reducer;
